import { useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import {
  DataGridPro,
  GridSortModel,
  GridColDef,
  DataGridProProps,
  GridEventListener,
  GridSortDirection,
} from '@mui/x-data-grid-pro';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useQuery } from 'react-query';

import { Box } from '@mui/material';
import { SxProps } from '@mui/system';

import { FilterButton, TableSearch } from '@molecules';
import { Axios, formatMidnightQueries, formatQueries, queryClient } from '@helpers';
import { FilterItem, MidnightQueries, Queries, TableSelectType } from '@services';
import { NoRowsOverlay } from '@atoms';
import { DEFAULT_QUERIES, useTableQueries } from '@hooks';
import { TableWrap } from '@templates';
import { SelectedRow } from './SelectedRow';
import { useNavigate } from 'react-router-dom';
import { UnSortedIcon } from '@atoms/Icons/UnSortedIcon';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { TQueryResponseCombineTable } from './types';
import {
  DEFAULT_TABLE_HEIGHT,
  TABLE_HEADER_HEIGHT,
  TABLE_SPACING_BOTTOM_HEIGHT,
} from './constants';
import { EDataTableType } from './enum';
import { handleGetDataBasedOnResponse } from './utils';
import { AxiosInstance } from 'axios';

interface TableProps<T> extends Omit<DataGridProProps, 'onRowClick' | 'rows'> {
  mocks?: T[];
  columns: GridColDef[];
  queryName?: string;
  apiClient?: AxiosInstance;
  apiRoute?: string;
  filtersList?: FilterItem[];
  onRowClick?: (row: T, event?: any) => void;
  selectedRowType?: TableSelectType;
  getSelectedValue?: (items: T[], selectedItems: string[]) => string;
  getSelectedMultipleValue?: (items: T[], selectedItems: string[]) => any;
  checkboxSelection?: boolean;
  refetch?: () => void;
  refetchQueryName?: string;
  searchPlaceholder?: string;
  withoutSearch?: boolean;
  preSelectedFilters?: { [key in string]: string | undefined | string[] };
  defaultQueries?: Partial<Queries>;
  tableSx?: SxProps;
  renderAdditionalControl?: ReactNode;
  renderRowPerPageOptions?: boolean;
  defaultPageSize?: number;
  renderCustomData?: (items: T[] | undefined) => T[] | any;
  formatData?: (items: T[] | undefined) => any;
  treeData?: boolean;
  getTreeDataPath?: (row: T) => string[];
  groupingColDef?: any;
  getRowClassName?: any;
  onCellClick?: GridEventListener<'cellClick'>;
  rowHeight?: number;
  checkHidePagination?: boolean;
  modelId?: string;
  triggerRefetch?: boolean;
  customHeight?: boolean;
  showFilterBySelect?: boolean;
  showSelectedRows?: boolean;
  hideFooterSelectedRowCount?: boolean;
  filterBySelectOptions?: { value: string; label: string }[];
  customSxForTable?: SxProps;
  dataTableWithoutCallingAPI?: T[];
  autocompleteClassname?: string;
  listBoxClassName?: string;
  popperClassName?: string;
  isMultipleSelect?: boolean;
  isMultipleSearch?: boolean;
  resetSelection?: boolean;
  isEnterprise?: boolean;
  customFilterSx?: SxProps;
  textSearchParams?: string;
  usingIsFetching?: boolean;
  usingMidnightResponse?: boolean;
  disableSearching?: boolean;
  customizeQueries?: (
    queries: Queries | MidnightQueries,
    modelId?: string,
  ) => { [key: string]: any };
}

export const Table = <T extends object>({
  mocks,
  queryName = 'name',
  apiClient,
  apiRoute = '',
  columns,
  filtersList,
  onRowClick,
  selectedRowType,
  getSelectedValue = () => '',
  // eslint-disable-next-line
  getSelectedMultipleValue = () => {},
  checkboxSelection,
  refetch: additionalRefetch,
  refetchQueryName,
  searchPlaceholder,
  withoutSearch,
  showFilterBySelect,
  showSelectedRows = true,
  hideFooterSelectedRowCount = true,
  filterBySelectOptions,
  preSelectedFilters,
  defaultQueries,
  tableSx,
  renderAdditionalControl,
  renderRowPerPageOptions = true,
  defaultPageSize,
  renderCustomData,
  formatData,
  treeData,
  getTreeDataPath,
  groupingColDef,
  getRowClassName,
  onCellClick,
  rowHeight = 60,
  checkHidePagination = false,
  modelId,
  triggerRefetch,
  customHeight,
  customSxForTable,
  dataTableWithoutCallingAPI,
  autocompleteClassname,
  listBoxClassName,
  popperClassName,
  isMultipleSelect = false,
  isMultipleSearch = false,
  resetSelection,
  isEnterprise = false,
  customFilterSx,
  textSearchParams,
  usingIsFetching,
  usingMidnightResponse = false,
  disableSearching,
  customizeQueries,
  ...others
}: TableProps<T>) => {
  const navigate = useNavigate();

  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);

  useEffect(() => {
    if (resetSelection) {
      setSelectedItems([]);
    }
  }, [resetSelection]);
  const {
    queries,
    setQueries,
    filterChange,
    resetFilter,
    filters,
    setFilters,
    selectedFilters,
    setSelectedFilters,
  } = useTableQueries(preSelectedFilters, defaultQueries, isEnterprise, usingMidnightResponse);

  const { data, isLoading, isFetching, refetch } = useQuery<
    unknown,
    unknown,
    TQueryResponseCombineTable<T>
  >([queryName, queries, filters, modelId], () => {
    if (!apiRoute) {
      return;
    }

    const client = apiClient || (isEnterprise ? ApiClientEnterprise : Axios);
    return client
      .get(apiRoute, {
        params: customizeQueries
          ? customizeQueries({ ...queries, ...filters }, modelId)
          : usingMidnightResponse
            ? formatMidnightQueries({ ...queries, ...filters } as MidnightQueries, modelId)
            : formatQueries({ ...queries, ...filters } as Queries, modelId),
      })
      .then((res) => (formatData ? formatData(res.data) : res.data));
  });

  useEffect(() => {
    refetch();
  }, [triggerRefetch, refetch]);

  const totalCount = useMemo(() => {
    if (data) {
      return handleGetDataBasedOnResponse<T>(
        data,
        usingMidnightResponse,
        EDataTableType.TotalCount,
      ) as number;
    }
    return 0;
  }, [data, usingMidnightResponse]);

  const dataItems = useMemo(() => {
    if (data) {
      return handleGetDataBasedOnResponse<T>(
        data,
        usingMidnightResponse,
        EDataTableType.DataItem,
      ) as T[];
    }
    return [];
  }, [data, usingMidnightResponse]);

  const size = useMemo(() => {
    return (
      (handleGetDataBasedOnResponse<T>(
        queries,
        usingMidnightResponse,
        EDataTableType.Size,
      ) as number) || 10
    );
  }, [queries, usingMidnightResponse]);

  const sortDirection = useMemo(() => {
    return handleGetDataBasedOnResponse<T>(
      queries,
      usingMidnightResponse,
      EDataTableType.SortDirection,
    ) as GridSortDirection;
  }, [queries, usingMidnightResponse]);

  const rowsPerPageOptions =
    others.rowsPerPageOptions ||
    (totalCount
      ? [10, 20, 50, 100, totalCount]
          .filter((item) => item <= totalCount)
          .filter((v, i, a) => a.indexOf(v) === i) // get unique
      : [10, 20, 50, 100]);

  const handleRefetch = () => {
    refetch();
    setSelectedItems([]);
    // eslint-disable-next-line no-unused-expressions
    additionalRefetch && additionalRefetch();
    if (refetchQueryName) {
      queryClient.refetchQueries(refetchQueryName);
    }
  };

  const sortModelChange = useCallback(([sortModel]: GridSortModel) => {
    const { sort, field } = sortModel || {};
    const sortOrderValue = sort || null;
    setQueries((prevQueries) => ({
      ...prevQueries,
      sortBy: field || '',
      ...(usingMidnightResponse
        ? { sortOrder: sortOrderValue }
        : { sortDirection: sortOrderValue }),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const noRows = useCallback(
    () => (
      <NoRowsOverlay
        filterText={queries.filterText}
        hasSelectedFilters={!!selectedFilters.length}
      />
    ),
    [queries.filterText, selectedFilters],
  );

  const handleData = () => {
    const page = queries.page || 0;
    return dataItems?.map((item: any, index) => ({
      index: (isEnterprise || usingMidnightResponse ? page - 1 : page) * size + index + 1,
      ...item,
    }));
  };

  const items =
    mocks ||
    dataTableWithoutCallingAPI ||
    (renderCustomData && renderCustomData(dataItems)) ||
    handleData() ||
    [];
  const hasSelectedFilters = filtersList?.some(({ key }) => selectedFilters.includes(key));
  const selected = getSelectedValue(items, selectedItems);
  const multipleSelected = getSelectedMultipleValue(items, selectedItems);

  const tableHeight = useMemo(() => {
    if (!totalCount) {
      return 300;
    } else if (totalCount <= DEFAULT_QUERIES.size) {
      return totalCount * rowHeight + TABLE_HEADER_HEIGHT + TABLE_SPACING_BOTTOM_HEIGHT;
    } else {
      return DEFAULT_TABLE_HEIGHT;
    }
  }, [rowHeight, totalCount]);

  const hidePagination = useMemo(() => {
    if (checkHidePagination && totalCount <= DEFAULT_QUERIES.size) {
      return true;
    }
    return false;
  }, [checkHidePagination, totalCount]);

  const onSearch = (filterText: string, filterTextBy: any) => {
    if (disableSearching) {
      return; // TODO: temporarily hide searching function
    }

    const searchParams = new URLSearchParams(window.location.search);
    const textSearchFilters = textSearchParams ? textSearchParams : 'filterText';

    searchParams.delete(textSearchFilters);
    if (filterText) {
      searchParams.set(textSearchFilters, filterText);
    }

    searchParams.delete('filterTextBy');
    if (isMultipleSelect && !!filterTextBy.length) {
      filterTextBy.forEach((filter: string) => searchParams.append('filterTextBy', filter));
    } else if (!isMultipleSelect && !!filterTextBy) {
      searchParams.append('filterTextBy', filterTextBy);
    }

    setQueries({ ...queries, page: isEnterprise || usingMidnightResponse ? 1 : 0 });
    navigate(`?${searchParams.toString()}`);
  };

  return (
    <>
      {!withoutSearch && (
        <Box display="flex" gap="16px" mb="24px" alignItems="flex-start">
          <TableSearch
            placeholder={searchPlaceholder}
            defaultSearch={queries.filterText}
            defaultSearchBy={queries.filterTextBy}
            showFilterBySelect={showFilterBySelect}
            filterBySelectOptions={filterBySelectOptions}
            autocompleteClassname={autocompleteClassname}
            listBoxClassName={listBoxClassName}
            popperClassName={popperClassName}
            isMultipleSelect={isMultipleSelect}
            isMultipleSearch={isMultipleSearch}
            onSearch={onSearch}
          />
          {filtersList && (
            <FilterButton
              customFilterSx={{ ...customFilterSx }}
              options={filtersList.filter(({ key }) => !selectedFilters.includes(key))}
              onSelect={(key) => setSelectedFilters([...selectedFilters, key])}
              hasSelected={hasSelectedFilters}
              disabled={!filtersList.filter(({ key }) => !selectedFilters.includes(key)).length}
            />
          )}
          {renderAdditionalControl}
        </Box>
      )}
      {filtersList && !!selectedFilters?.length && (
        <Box display="flex" gap="24px" mb="24px">
          {filtersList?.map(({ key, getFilter, resetFields }) => {
            if (selectedFilters?.includes(key)) {
              return getFilter(
                filterChange,
                () => {
                  resetFilter(resetFields || [key]);
                  setSelectedFilters(selectedFilters.filter((f) => f !== key));
                },
                setFilters,
                filters,
              );
            }

            return null;
          })}
        </Box>
      )}
      {showSelectedRows && !!selectedItems?.length && (
        <SelectedRow
          value={selected}
          multipleValue={multipleSelected}
          items={selectedItems}
          count={selectedItems.length}
          cancel={() => setSelectedItems([])}
          refetch={handleRefetch}
          type={selectedRowType}
        />
      )}
      <TableWrap height={`${customHeight ? tableHeight : DEFAULT_TABLE_HEIGHT}px`} sx={tableSx}>
        <DataGridPro
          rows={items}
          columns={columns}
          loading={usingIsFetching ? isFetching : isLoading}
          pageSize={defaultPageSize || size}
          page={isEnterprise ? queries.page - 1 : queries.page}
          onSortModelChange={sortModelChange}
          sortModel={
            queries.sortBy && sortDirection ? [{ field: queries.sortBy, sort: sortDirection }] : []
          }
          rowCount={totalCount || 0}
          onPageChange={(page) => setQueries({ ...queries, page: isEnterprise ? page + 1 : page })}
          rowsPerPageOptions={renderRowPerPageOptions ? rowsPerPageOptions : []}
          pagination={totalCount !== 0}
          onPageSizeChange={(size) =>
            setQueries({
              ...queries,
              ...(usingMidnightResponse
                ? { pageSize: size, page: isEnterprise || usingMidnightResponse ? 1 : 0 }
                : { size, page: isEnterprise || usingMidnightResponse ? 1 : 0 }),
            })
          }
          onRowClick={({ row }, event) => onRowClick && onRowClick(row, event)}
          onCellClick={onCellClick}
          components={{
            NoRowsOverlay: noRows,
            ColumnSortedAscendingIcon: () => <ArrowDropUpIcon sx={{ color: '#44AEFB' }} />,
            ColumnSortedDescendingIcon: () => <ArrowDropDownIcon sx={{ color: '#44AEFB' }} />,
            ColumnUnsortedIcon: () => <UnSortedIcon />,
            ColumnMenuIcon: () => <FilterAltIcon fontSize="small" sx={{ color: '#44AEFB' }} />,
          }}
          onSelectionModelChange={(ids) => setSelectedItems(ids as string[])}
          selectionModel={selectedItems}
          checkboxSelection={checkboxSelection}
          filterMode="server"
          sortingMode="server"
          paginationMode="server"
          hideFooterPagination={hidePagination}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          hideFooterSelectedRowCount={hideFooterSelectedRowCount}
          keepNonExistentRowsSelected
          treeData={treeData}
          getTreeDataPath={getTreeDataPath}
          groupingColDef={groupingColDef}
          getRowClassName={getRowClassName}
          rowHeight={rowHeight}
          sx={{
            '& .MuiDataGrid-iconButtonContainer': {
              visibility: 'visible',
            },
            '& .MuiDataGrid-sortIcon': {
              opacity: 'inherit !important',
            },
            ...customSxForTable,
          }}
          {...others}
        />
      </TableWrap>
    </>
  );
};
